import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputBase,
	List,
	ListItem,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import * as React from 'react';
import { SyntheticEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthContext } from '../../../../../../context/auth';
import { dayjs } from '../../../../../../date-util';
import colors from '../../../../../../util/palette';
import ResourcesAPI, {
	ResourceResponse,
	ResourceType,
} from '../../../api/resources';
import {
	DATE_COMPLIANCE_ADD_MORE_FILES,
	DATE_COMPLIANCE_CONFIRM_DELETE_MODAL_CANCEL_BUTTON,
	DATE_COMPLIANCE_CONFIRM_DELETE_MODAL_DELETE_BUTTON,
	DATE_COMPLIANCE_CONFIRM_DELETE_MODAL_HEADER,
	DATE_COMPLIANCE_CONFIRM_DELETE_MODAL_MESSAGE,
	DATE_COMPLIANCE_FAILED_TO_FETCH_RESOURCES,
	DATE_COMPLIANCE_LOADING_MESSAGE,
	DATE_COMPLIANCE_NO_RESOURCES_FOUND_FILES,
	DATE_COMPLIANCE_ROW_DATE_FORMAT,
	DATE_COMPLIANCE_SEARCH_FILES,
	DATE_COMPLIANCE_SEARCH_NO_RESULTS,
	DATE_COMPLIANCE_TYPE_DOCUMENT_TEMPLATE,
	DATE_COMPLIANCE_TYPE_FORMULA_TEMPLATE,
	DATE_COMPLIANCE_TYPE_STATIC_DOC,
	DATE_COMPLIANCE_TYPE_TABLE,
	MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH,
} from '../../../data/constants';
import { handleModalClose } from '../../../utils/handleModalClose';
import { RESOURCES_KEY } from '../ResourceGroups';
import DeployResourcesModal from './DeployResourcesModal';

interface ResourcesListProps {
	product: string;
	effectiveTimestamp: number;
}

const messageByResourceTypePlural = {
	[ResourceType.TABLES]: DATE_COMPLIANCE_TYPE_TABLE(true),
	[ResourceType.STATIC_DOCUMENTS]: DATE_COMPLIANCE_TYPE_STATIC_DOC(true),
	[ResourceType.DOCUMENT_TEMPLATES]:
		DATE_COMPLIANCE_TYPE_DOCUMENT_TEMPLATE(true),
	[ResourceType.FORMULA_TEMPLATES]: DATE_COMPLIANCE_TYPE_FORMULA_TEMPLATE(true),
};

const messageByResourceType = {
	[ResourceType.TABLES]: DATE_COMPLIANCE_TYPE_TABLE(),
	[ResourceType.STATIC_DOCUMENTS]: DATE_COMPLIANCE_TYPE_STATIC_DOC(),
	[ResourceType.DOCUMENT_TEMPLATES]: DATE_COMPLIANCE_TYPE_DOCUMENT_TEMPLATE(),
	[ResourceType.FORMULA_TEMPLATES]: DATE_COMPLIANCE_TYPE_FORMULA_TEMPLATE(),
};

const ResourcesList = (props: ResourcesListProps) => {
	const { tenantHostname } = useParams();
	const authData = useAuthContext();
	const authToken = authData?.tenantAuthorizationData?.authorizationToken;
	const tenantId = authData?.tenantAuthorizationData?.tenantId;
	const { product, effectiveTimestamp } = props;
	const [activeResourceType, setActiveResourceType] =
		useState<ResourceType>(null);
	const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] =
		useState<boolean>(false);
	const [deployModalOpen, setDeployModalOpen] = useState<boolean>(false);
	const [searchInput, setSearchInput] = useState<string>('');
	const [resourcePendingDelete, setResourcePendingDelete] = useState<any>();

	const { data, isLoading, isError, error } = ResourcesAPI.useResources(
		[
			RESOURCES_KEY,
			tenantId || tenantHostname,
			product,
			effectiveTimestamp.toString(),
			activeResourceType,
		],
		{
			productName: product,
			resourceType: activeResourceType,
			asOfTimestamp: effectiveTimestamp,
			timestampMatchMode: 'EXACT',
		},
		authToken,
		tenantHostname,
	);

	const { mutateAsync: deleteResource, isLoading: isDeleting } =
		ResourcesAPI.useDeleteResourceMutation(
			[
				RESOURCES_KEY,
				tenantId || tenantHostname,
				product,
				effectiveTimestamp.toString(),
				activeResourceType,
			],
			[RESOURCES_KEY, tenantId || tenantHostname, product],
			authToken,
			tenantHostname,
		);

	const handleConfirmDeleteModalOpen = (selectedResource: ResourceResponse) => {
		setConfirmDeleteModalOpen(true);
		setResourcePendingDelete(selectedResource);
	};

	const handleConfirmDeleteModalClose = () => {
		setConfirmDeleteModalOpen(false);
		setResourcePendingDelete(null);
	};

	const handleDeleteResource = async (resource: ResourceResponse) => {
		await deleteResource({
			resourceType: activeResourceType,
			locator: resource.locator,
			productName: product,
		});
		handleConfirmDeleteModalClose();
	};

	const handleAddMore = () => {
		setDeployModalOpen(true);
		setSearchInput('');
	};

	const handleTabChange = (e: SyntheticEvent, value: any) => {
		setSearchInput('');
		setActiveResourceType(value);
	};

	if (!isLoading && !activeResourceType) {
		setActiveResourceType(data?.resourceType || ResourceType.TABLES);
	}

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInput(event.target.value);
	};

	const resourcesList = searchInput?.length
		? data?.resources.filter(
				(r) => r.name.toLowerCase().indexOf(searchInput.toLowerCase()) !== -1,
		  )
		: data?.resources;

	const renderTabs = () => {
		return (
			<Stack direction='row' spacing={1}>
				<Tabs
					value={activeResourceType}
					onChange={handleTabChange}
					textColor='secondary'
					indicatorColor='secondary'
					aria-label='primary tabs resource types'
				>
					<Tab
						id={`Tabs__Tab--${DATE_COMPLIANCE_TYPE_TABLE(true)}`}
						value={ResourceType.TABLES}
						label={DATE_COMPLIANCE_TYPE_TABLE(true)}
					/>
					<Tab
						id={`Tabs__Tab--${DATE_COMPLIANCE_TYPE_STATIC_DOC(true)}`}
						value={ResourceType.STATIC_DOCUMENTS}
						label={DATE_COMPLIANCE_TYPE_STATIC_DOC(true)}
					/>
					<Tab
						value={ResourceType.DOCUMENT_TEMPLATES}
						label={DATE_COMPLIANCE_TYPE_DOCUMENT_TEMPLATE(true)}
					/>
					<Tab
						value={ResourceType.FORMULA_TEMPLATES}
						label={DATE_COMPLIANCE_TYPE_FORMULA_TEMPLATE(true)}
					/>
				</Tabs>
				<Button
					color='secondary'
					variant='text'
					startIcon={<AddIcon />}
					onClick={handleAddMore}
				>
					{DATE_COMPLIANCE_ADD_MORE_FILES()}
				</Button>
				<Box
					sx={{ display: 'flex', alignItems: 'center', paddingLeft: '16px' }}
				>
					<SearchIcon
						style={{ marginRight: '3px', color: colors.black.lighter }}
					/>
					<InputBase
						value={searchInput}
						onChange={handleSearchChange}
						placeholder={DATE_COMPLIANCE_SEARCH_FILES()}
					/>
				</Box>
			</Stack>
		);
	};

	const renderBody = () => {
		if (isLoading) {
			return (
				<Stack
					mt={4}
					mb={2}
					spacing={2}
					sx={{
						width: '100%',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<CircularProgress color='secondary' />
					{DATE_COMPLIANCE_LOADING_MESSAGE()}
				</Stack>
			);
		}

		if (isError) {
			return (
				<Stack
					mt={4}
					mb={2}
					spacing={2}
					sx={{
						width: '100%',
						justifyContent: 'center',
					}}
				>
					<Alert severity='error'>
						{DATE_COMPLIANCE_FAILED_TO_FETCH_RESOURCES(
							messageByResourceType[activeResourceType]?.toLowerCase(),
							error,
						)}
					</Alert>
				</Stack>
			);
		}

		return (
			<>
				{data && data.resources.length > 0 && resourcesList.length === 0 && (
					<Typography
						variant='h4'
						sx={{ paddingTop: '10px', paddingLeft: '16px' }}
					>
						{DATE_COMPLIANCE_SEARCH_NO_RESULTS(
							messageByResourceTypePlural[activeResourceType]?.toLowerCase(),
							searchInput,
						)}
					</Typography>
				)}
				{data && data.resources.length === 0 && (
					<Typography
						variant='h4'
						sx={{ paddingTop: '10px', paddingLeft: '16px' }}
					>
						{DATE_COMPLIANCE_NO_RESOURCES_FOUND_FILES(
							messageByResourceTypePlural[activeResourceType]?.toLowerCase(),
							dayjs(effectiveTimestamp).format(
								DATE_COMPLIANCE_ROW_DATE_FORMAT(),
							),
						)}
					</Typography>
				)}
				{resourcesList && resourcesList.length > 0 && (
					<List dense={true}>
						{resourcesList
							?.sort(function (a, b) {
								if (a.name < b.name) {
									return -1;
								}
								if (a.name > b.name) {
									return 1;
								}
								return 0;
							})
							.map((resource) => (
								<ListItem
									key={resource.locator}
									secondaryAction={
										<>
											<IconButton
												sx={{
													marginRight: '5px',
												}}
												href={resource.url}
												edge='end'
												aria-label='download'
											>
												<DownloadIcon />
											</IconButton>
											<IconButton
												onClick={() => handleConfirmDeleteModalOpen(resource)}
												edge='end'
												aria-label='delete'
											>
												<DeleteIcon />
											</IconButton>
										</>
									}
								>
									{resource.name}
								</ListItem>
							))}
						<Dialog
							open={confirmDeleteModalOpen}
							onClose={(e, r) =>
								handleModalClose(r, handleConfirmDeleteModalClose)
							}
						>
							<DialogTitle>
								{DATE_COMPLIANCE_CONFIRM_DELETE_MODAL_HEADER(
									messageByResourceType[activeResourceType],
								)}
							</DialogTitle>
							<DialogContent sx={{ width: MODULAR_CONFIG_UPLOAD_DIALOG_WIDTH }}>
								{DATE_COMPLIANCE_CONFIRM_DELETE_MODAL_MESSAGE(
									resourcePendingDelete?.name,
									messageByResourceType[
										activeResourceType || ResourceType.TABLES
									],
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleConfirmDeleteModalClose}
									color='neutralButton'
								>
									{DATE_COMPLIANCE_CONFIRM_DELETE_MODAL_CANCEL_BUTTON()}
								</Button>
								<Button
									variant='contained'
									color='error'
									disabled={isDeleting}
									onClick={() => handleDeleteResource(resourcePendingDelete)}
								>
									{DATE_COMPLIANCE_CONFIRM_DELETE_MODAL_DELETE_BUTTON()}
								</Button>
							</DialogActions>
						</Dialog>
					</List>
				)}
			</>
		);
	};

	return (
		<Stack>
			<Stack direction='column' height='100%' spacing={2}>
				{activeResourceType && renderTabs()}
				{renderBody()}
			</Stack>
			<DeployResourcesModal
				open={deployModalOpen}
				product={product}
				resourceType={activeResourceType}
				effectiveTimestamp={effectiveTimestamp}
				handleClose={() => setDeployModalOpen(false)}
				isProductEditable={false}
			/>
		</Stack>
	);
};

export default ResourcesList;
